// TeacherView fetches the applicants who designated the signed-in recommender.
//
// History: #143 migrated this off the v5 `API.graphql(getRecommender)` bundle
// to v6 `Recommender.get({ email })` + four lazy `hasMany` loaders
// (`prodApplicants{1,2,3}()` / `schoolReportProdApplicants()`). #153 then found
// those connection loaders 400 under #141 owner-auth: AppSync resolves each as
// a Query on the relationship GSI (partition key = the slot email field) and
// injects #141's owner authFilter (`<slotEmail> = claims.email`) as a
// FilterExpression, but DynamoDB forbids a FilterExpression on a key attribute
// of the queried index -> `DynamoDbException` on every traversal.
//
// A first attempt (`ProdApplicant.list()`) also failed live: the deployed
// aws-amplify data client sends the Cognito ACCESS token, which on this
// email-login pool carries `sub`/`cognito:groups` but NO `email` claim, so
// #141's owner-auth (keyed on the `email` claim) matched nothing and the list
// came back empty.
//
// Current approach (#153): call the custom `listRecommenderApplicants` query.
// Its backing Lambda (recommender-handler/list_applicants/app.py) resolves the
// caller's email SERVER-SIDE -- preferring the `email` claim, else reverse-
// looking-up `sub` via `cognito-idp:AdminGetUser` -- so it is token-agnostic
// (works for the deployed access token AND ID-token callers) and returns
// exactly the applicant rows that designate this recommender in any of the
// four slots. We then determine which slot(s) the recommender occupies on each
// applicant to build the per-slot upload UI.
import { useState, useEffect, Fragment } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Snackbar from "@mui/material/Snackbar";
import {
  Recommendee,
  RecommendationTarget,
  RecommendationFileType,
} from "../types";
import { useAuthenticator } from "@aws-amplify/ui-react";
import RecommendationSideBar from "../components/RecommendationSideBar";
import RecommendationViewFile from "../components/RecommendationViewFile";
import RecommendationUploadFile from "../components/RecommendationUploadFile";
import RecommendationHome from "../components/RecommendationHome";
import Footer from "../components/Footer";
import {
  findRecommendeeIndex,
  getFileKey,
  getPresignedUrl,
} from "../utils";
import { useLang } from "../LanguageContextProvider";
import { getClient } from "../amplify-client";
import { resolveUserEmail } from "../auth-user";
import Spinner from "../components/Spinner";

// The data client is constructed lazily at use-time via `getClient()`
// (see `amplify-client.ts`), never at module scope -- a module-scope
// `generateClient()` snapshots the Amplify config before
// `Amplify.configure(...)` runs whenever the bundler evaluates this module
// first, which is the deployed-bundle `Amplify has not been configured`
// regression this fix removes. Untyped pending the TS 5 bump tracked in
// #146.

// Maps each upload slot (the RecommendationFileType key) to the ProdApplicant
// fields that designate the recommender for that slot (email) and carry the
// recommender's own display name (name).
const SLOT_FIELDS: {
  key: RecommendationFileType;
  emailField:
    | "recommender1Email"
    | "recommender2Email"
    | "recommender3Email"
    | "schoolReportWriterEmail";
  nameField:
    | "recommender1Name"
    | "recommender2Name"
    | "recommender3Name"
    | "schoolReportWriterName";
}[] = [
  { key: "rec1", emailField: "recommender1Email", nameField: "recommender1Name" },
  { key: "rec2", emailField: "recommender2Email", nameField: "recommender2Name" },
  { key: "rec3", emailField: "recommender3Email", nameField: "recommender3Name" },
  {
    key: "report",
    emailField: "schoolReportWriterEmail",
    nameField: "schoolReportWriterName",
  },
];

const retrieveData = async (
  email: string,
): Promise<{ name?: string; data: Recommendee[] }> => {
  // The custom `listRecommenderApplicants` query returns exactly the
  // applicant rows that designate this recommender (the Lambda OR-scans the
  // four slot fields server-side after resolving the caller's email from the
  // JWT), so there is no client-side pagination/union/authFilter to manage --
  // it sidesteps both the access-token-has-no-email owner-auth gap and the
  // hasMany-connection FilterExpression-on-GSI-key 400.
  const client = getClient();
  const { data: applicants } = (await client.queries.listRecommenderApplicants(
    {},
  )) as { data: Array<Record<string, any> | null> | null };

  // Normalize the caller's email the same way the server does (ingestion
  // stores slot emails lower-cased; the Lambda lower-cases the claim before
  // scanning). resolveUserEmail() returns the raw Cognito email attribute,
  // which may be mixed-case -- comparing it raw against the lower-cased slot
  // fields would drop every row for such a recommender and silently reproduce
  // the empty-TeacherView symptom #153 fixes (just one layer up). #153.
  const normalizedEmail = email.trim().toLowerCase();

  // The recommender's own display name (for the greeting) is carried on each
  // applicant row in the matching slot's `recommender*Name` field; take it from
  // the first matching slot.
  let recommenderName: string | undefined;

  const recommendees = await Promise.all(
    (applicants ?? [])
      .filter((applicant): applicant is Record<string, any> => applicant != null)
      .map((applicant) => ({
        applicant,
        // Determine WHICH slot(s) the recommender occupies on this row -- the
        // row itself doesn't say -- so we can render the right document
        // type(s). An applicant may designate the same teacher in more than
        // one slot (e.g. recommender1 AND school-report writer), yielding one
        // card with multiple doc slots. (The server already scoped the list to
        // this recommender; this is the slot-attribution step, not an auth
        // re-check.) Compare lower-cased on both sides (see normalizedEmail).
        matchedSlots: SLOT_FIELDS.filter(
          ({ emailField }) =>
            (applicant[emailField] ?? "").toLowerCase() === normalizedEmail,
        ),
      }))
      .filter(({ matchedSlots }) => matchedSlots.length > 0)
      .map(async ({ applicant, matchedSlots }) => {
        const applicantNum = applicant.applicantNum.toString();
        const name: string = applicant.name;
        const englishName: string = applicant.englishName;
        const documentsUploaded: Recommendee["documentsUploaded"] = {};
        await Promise.all(
          matchedSlots.map(async ({ key, nameField }) => {
            if (!recommenderName && applicant[nameField]) {
              recommenderName = applicant[nameField];
            }
            const fileUrl = await getPresignedUrl(
              getFileKey({ recommendee: { applicantNum, name }, fileType: key }),
            );
            const { status } = await fetch(fileUrl);
            documentsUploaded[key] = {
              assignee: email,
              fileUrl: status === 200 ? fileUrl : null,
            };
          }),
        );
        return { applicantNum, name, englishName, documentsUploaded, imageUrl: "" };
      }),
  );

  return { name: recommenderName, data: recommendees };
};

const TeacherView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<Recommendee[]>([]);
  const [name, setName] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isReuploadIntentActive, setIsReuploadIntentActive] = useState(false);

  const { user } = useAuthenticator((context) => [context.user]);
  const [selectedTarget, setSelectedTarget] =
    useState<RecommendationTarget | null>(null);
  // The recommender's email keys every lookup below. In the Gen 2 email-login
  // pool `user.username` is the sub UUID, not the email, so resolve it from the
  // user attributes (see resolveUserEmail). Empty until resolved; the data
  // effect below waits for it, and isLoading starts true so the Spinner shows
  // until both the email and the data have loaded.
  const [email, setEmail] = useState<string>("");
  const { lang } = useLang();

  useEffect(() => {
    let cancelled = false;
    resolveUserEmail(user).then((resolved) => {
      if (!cancelled) setEmail(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!email) return;
    setIsLoading(true);
    setHasError(false);
    retrieveData(email)
      .then(({ name, data }) => {
        setData(data);
        if (name) setName(name);
      })
      .catch((err) => {
        // listRecommenderApplicants is now the sole server-side authz gate;
        // its Lambda raises UnauthorizedError for an unresolvable identity,
        // which surfaces here as a rejected promise. Without this catch the
        // recommender would hang on the Spinner forever (isLoading was only
        // cleared on success). Surface a minimal error state instead. #153.
        console.error("Failed to load recommender applicants", err);
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [email]);

  useEffect(() => {
    setIsReuploadIntentActive(false);
  }, [selectedTarget]);

  const setFileUrl = async (target: RecommendationTarget, key: string) => {
    const index = findRecommendeeIndex(data, target.recommendee);
    const recommendee = data[index];
    const { documentsUploaded } = recommendee;
    const url = await getPresignedUrl(key);
    setData([
      ...data.slice(0, index),
      {
        ...recommendee,
        documentsUploaded: {
          ...documentsUploaded,
          [target.fileType]: {
            ...documentsUploaded[target.fileType],
            fileUrl: url,
          },
        },
      },
      ...data.slice(index + 1),
    ]);
    setSelectedTarget(null);
    setIsSnackbarOpen(true);
  };

  const handleClose = () => setSelectedTarget(null);
  const handleSnackbarClose = () => setIsSnackbarOpen(false);

  const renderContent = () => {
    if (!selectedTarget)
      return (
        <RecommendationHome
          name={name}
          data={data}
          email={email}
          setSelectedTarget={setSelectedTarget}
        />
      );
    const file =
      selectedTarget.recommendee.documentsUploaded[selectedTarget.fileType];
    if (file && file.fileUrl && !isReuploadIntentActive)
      return (
        <RecommendationViewFile
          target={selectedTarget}
          onClose={handleClose}
          setIsReuploadIntentActive={setIsReuploadIntentActive}
        />
      );
    return (
      <RecommendationUploadFile
        target={selectedTarget}
        onClose={handleClose}
        setFileUrl={setFileUrl}
      />
    );
  };

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      <Container
        disableGutters
        sx={{
          // height: '100%',
          flex: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          display: "flex",
          overflow: "hidden",
        }}
      >
        {isLoading ? (
          <Spinner size={96} />
        ) : hasError ? (
          <Box flex={1} p={3} display="flex" alignItems="center">
            <Alert severity="error" sx={{ width: "100%" }}>
              {lang === "ja"
                ? "応募者データの読み込みに失敗しました。ページを再読み込みしてください。問題が続く場合は it@grew-bancroft.or.jp までご連絡ください。"
                : "Failed to load applicant data. Please reload the page. If the problem persists, contact it@grew-bancroft.or.jp."}
            </Alert>
          </Box>
        ) : (
          <Fragment>
            <RecommendationSideBar
              data={data}
              selectedTarget={selectedTarget}
              setSelectedTarget={setSelectedTarget}
            />
            <Box flex={1} height="100%" overflow="auto">
              {renderContent()}
            </Box>
          </Fragment>
        )}
      </Container>
      {/* <p>
        本年度の応募は2025/09/10 (水)
        23:59を以て締め切りました。お問い合わせはapply@grew-bancroft.or.jpまでお願いいたします。
        <br />
        Application submission deadline 2025/09/08 has passed. Please contact
        apply@grew-bancroft.or.jp for further inquiries.
      </p> */}
      <Footer />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          severity="success"
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {lang === "en"
            ? "File uploaded successfully. Please check the content of your uploaded file from the left menu. You can also re-upload the document. If you encounter any issues, please read this guide and contact us: https://grew-bancroft.or.jp/description_en/"
            : "アップロードが完了しました。念の為、左画面からアップロードしたPDFの内容が正しいか必ず確認してください。再度アップロードも可能です。うまくアップロードできない場合は、こちらのガイドに従ってお問合せください: https://grew-bancroft.or.jp/description2/"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherView;
